import React, { useMemo, useEffect, useState } from 'react';
import { Link } from '../../snippets';
import { Svg } from '../../snippets';

export function ProductBreadcrumbs({
  breadcrumbs,
  product,
  enabledProductCarousel,
  setGroupedBreadcrumbs,
}) {
  const { mens, womens } = breadcrumbs ?? {};
  const productType = product?.productType;

  const [breadcrumbData, setBreadcrumbData] = useState({
    parent: { title: '', handle: '' },
    main: { title: '', handle: '' },
    collection: { title: '', handle: '' }
  });

  const findCollectionKey = (mens, womens, value, keyType) => {
    if ((!mens && !womens) || !value) return null;

    const searchCollections = (obj) => {
      for (const key in obj) {
        const collections = obj[key]?.collections;
        if (collections) {
          for (const item of collections) {
            const nestedValue = keyType.split('.').reduce((acc, part) => acc && acc[part], item);
            if (nestedValue === value) {
              return key;
            }
          }
        }
      }
      return null;
    };

    const mensKey = searchCollections(mens);
    if (mensKey) return { key: mensKey, type: 'mens' };

    const womensKey = searchCollections(womens);
    if (womensKey) return { key: womensKey, type: 'womens' };

    return null;
  };

  const findMainKey = (mens, womens, value, keyType) => {
    if ((!mens && !womens) || !value) return null;
  
    const searchCollections = (obj) => {
      for (const key in obj) {
        if (obj[key] && obj[key].collection) {
          const nestedValue = keyType.split('.').reduce((acc, part) => acc && acc[part], obj[key].collection);
          if (nestedValue === value) {
            return key;
          }
        }
      }
      return null;
    };
  
    const mensKey = searchCollections(mens);
    if (mensKey) return { key: mensKey, type: 'mens' };
  
    const womensKey = searchCollections(womens);
    if (womensKey) return { key: womensKey, type: 'womens' };
  
    return null;
  };

  const collectionKeyInfo = useMemo(() => findCollectionKey(mens, womens, breadcrumbData.collection.handle, 'collection.url'), [mens, womens, breadcrumbData.collection.handle]);
  const isCollectionHandleInMensOrWomens = collectionKeyInfo !== null;
  const productTypeKeyInfo = useMemo(() => findCollectionKey(mens, womens, productType, 'productType'), [mens, womens, productType]);
  const isProductTypeInMensOrWomens = productTypeKeyInfo !== null;
  const mainKeyInfo = useMemo(() => findMainKey(mens, womens, breadcrumbData.collection.handle, 'url'), [mens, womens, breadcrumbData.collection.handle]);
  const isMainHandleInMensOrWomens = mainKeyInfo !== null;

  const handleParentCollection = (parentKeyInfo, parentObj) => {
    const parentCollection = parentKeyInfo?.type === 'mens' ? mens?.collection : womens?.collection;
    if (parentCollection) {
      setBreadcrumbData(prevData => ({
        ...prevData,
        parent: { title: parentCollection.text, handle: parentCollection.url }
      }));
    } else {
      setBreadcrumbData(prevData => ({
        ...prevData,
        parent: { title: 'Home', handle: '/' }
      }));
    }
    const parentKey = parentKeyInfo?.key;
    if (parentKey && parentObj[parentKey]?.collection?.url) {
      setBreadcrumbData(prevData => ({
        ...prevData,
        main: { title: parentObj[parentKey].collection.text, handle: parentObj[parentKey].collection.url }
      }));
    } else {
      setBreadcrumbData(prevData => ({
        ...prevData,
        main: { title: '', handle: '' }
      }));
    }
  };

  useEffect(() => {
    const savedCollectionTitle = sessionStorage.getItem('savedCollectionTitle');
    const savedCollectionHandle = sessionStorage.getItem('savedCollectionHandle');
    const savedHistory = sessionStorage.getItem('savedHistory');
    if (savedCollectionTitle && savedCollectionHandle) {
      const groupedBreadcrumbs = { title: savedCollectionTitle, handle: savedCollectionHandle, path: savedHistory };
      setGroupedBreadcrumbs(groupedBreadcrumbs);
      
      setBreadcrumbData(prevData => ({
        ...prevData,
        collection: { title: savedCollectionTitle, handle: `/collections/${savedCollectionHandle}` }
      }));
      if (isCollectionHandleInMensOrWomens) {
        handleParentCollection(collectionKeyInfo, collectionKeyInfo?.type === 'mens' ? mens : womens);
      } else {
        if (isMainHandleInMensOrWomens) {
          const parentCollection = mainKeyInfo?.type === 'mens' ? mens?.collection : womens?.collection;
          setBreadcrumbData(prevData => ({
            ...prevData,
            parent: { title: parentCollection?.text, handle: parentCollection?.url }
          }));
        } else {
          setBreadcrumbData(prevData => ({
            ...prevData,
            parent: { title: 'Home', handle: '/' }
          }));
        }
      }
      // Clean the localStorage items
      setTimeout(() => {
        sessionStorage.removeItem('savedCollectionTitle');
        sessionStorage.removeItem('savedCollectionHandle');
      }, 0);
    } else {
      const groupedBreadcrumbs = { title: '', handle: '', path: savedHistory };
      setGroupedBreadcrumbs(groupedBreadcrumbs);
      if (isProductTypeInMensOrWomens) {
        handleParentCollection(productTypeKeyInfo, productTypeKeyInfo?.type === 'mens' ? mens : womens);
        const parentKey = productTypeKeyInfo?.key;
        const parentObj = productTypeKeyInfo?.type === 'mens' ? mens : womens;
        const collectionItem = parentObj[parentKey]?.collections?.find(item => item?.productType === productType);
        if (collectionItem) {
          setBreadcrumbData(prevData => ({
            ...prevData,
            collection: { title: collectionItem?.collection?.text, handle: collectionItem?.collection?.url }
          }));
        } else {
          setBreadcrumbData(prevData => ({
            ...prevData,
            collection: { title: '', handle: '' }
          }));
        }
      } else {
        setBreadcrumbData({
          parent: { title: 'Home', handle: '/' },
          main: { title: '', handle: '' },
          collection: { title: '', handle: '' }
        });
      }
    } 
  }, [isCollectionHandleInMensOrWomens, isProductTypeInMensOrWomens, isMainHandleInMensOrWomens, mens, womens]);

  const breadcrumbAvailable = breadcrumbData?.collection?.handle !== '' || breadcrumbData?.main?.handle !== '';

  return breadcrumbAvailable && (
    <div
      className={`shadow-hairline-b mb-[.5px] flex py-1 md:-mb-5 md:py-3.5 md:shadow-none xl:-mb-10 ${
        enabledProductCarousel
          ? 'px-contained max-md:px-5'
          : 'px-5 md:px-5 xl:px-10'
      }`}
    >
      <div
        className={`flex flex-row items-center gap-2.5 text-sm md:text-base ${
          enabledProductCarousel
            ? 'mx-auto w-full max-w-[var(--content-max-lg-width)]'
            : ''
        }`}
      >
        {['parent', 'main', 'collection'].map((key, index) => (
          breadcrumbData[key].title && breadcrumbData[key].handle && breadcrumbData[key].title !== 'undefined' && breadcrumbData[key].handle !== '/collections/undefined' && (
            <React.Fragment key={key}>
              {index > 0 && (
                <Svg
                  className="w-3 text-mediumGray"
                  src="/svgs/chevron-right.svg#chevron-right"
                  alt="Right Chevron Icon"
                  title="Chevron"
                  viewBox="0 0 24 24"
                />
              )}
              <Link
                aria-label={breadcrumbData[key].title}
                className="text-sm text-mediumGray hover:text-primary"
                href={breadcrumbData[key].handle}
              >
                {breadcrumbData[key].title}
              </Link>
            </React.Fragment>
          )
        ))}
      </div>
    </div>
  );
}

ProductBreadcrumbs.displayName = 'ProductBreadcrumbs';